import { parsePmTaskUrl } from '../shared/pm-task-links.js';
import type { PmTaskPreview } from '../shared/pm-task-preview.js';

/** Inspect rendered links only; code, reply previews and unrelated PM pages stay ordinary text. */
export function pmTaskLinks(body: HTMLElement): { id: string; url: string }[] {
  const links = new Map<string, { id: string; url: string }>();
  for (const link of body.querySelectorAll<HTMLAnchorElement>('a[href][data-pm-task-id]')) {
    if (link.closest('code,pre,.reply-source')) continue;
    const task = parsePmTaskUrl(link.getAttribute('href') ?? '');
    if (task && link.dataset.pmTaskId === task.id && !links.has(task.id)) links.set(task.id, task);
    if (links.size >= 8) break;
  }
  return [...links.values()];
}

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const result = document.createElement(tag); result.className = className;
  if (text !== undefined) result.textContent = text;
  return result;
}
function picture(name: string, url: string | null, className: string): HTMLElement {
  const host = node('span', className, (Array.from(name.trim())[0] ?? '').toLocaleUpperCase('ru'));
  host.setAttribute('aria-hidden', 'true');
  if (url) {
    const image = node('img', ''); image.alt = ''; image.width = 28; image.height = 28;
    image.referrerPolicy = 'no-referrer'; image.src = url;
    image.addEventListener('error', () => image.remove(), { once: true });
    host.append(image);
  }
  return host;
}
const number = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
export const taskHours = (value: number | null): string => value === null ? '—' : `${number.format(value)} ч`;
export function taskEffort(effort: PmTaskPreview['effort']): string {
  if (!effort) return 'Часы —';
  const value = (hours: number | null): string => hours === null ? '—' : number.format(hours);
  const rest = effort.overrun && effort.overrun > 0 ? `+${value(effort.overrun)}` : value(effort.remaining);
  return `Часы ${value(effort.spent)} из ${value(effort.estimate)} (${rest})`;
}
export function taskEffortLabel(effort: PmTaskPreview['effort']): string {
  if (!effort) return 'Затрачено —';
  const spent = `Затрачено ${taskHours(effort.spent)}`;
  if (effort.estimate === null) return `${spent} (оценка не указана)`;
  const rest = effort.overrun && effort.overrun > 0
    ? `перерасход ${taskHours(effort.overrun)}` : `осталось ${taskHours(effort.remaining)}`;
  return `${spent} из ${taskHours(effort.estimate)} (${rest})`;
}
/** DTO dates and Moscow use UTC+03:00; compare calendar days, not elapsed 24-hour spans. */
export function taskDeadline(value: string | null, now = Date.now(), completed = false): string | null {
  if (value === null) return null;
  const day = (time: number): number => Math.floor((time + 3 * 60 * 60 * 1000) / 86_400_000);
  const difference = day(Date.parse(value)) - day(now);
  if (difference === 0) return 'сегодня';
  const count = Math.abs(difference), mod10 = count % 10, mod100 = count % 100;
  const word = mod10 === 1 && mod100 !== 11 ? 'день'
    : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'дня' : 'дней';
  if (completed && difference < 0) return `срок был ${count} ${word} назад`;
  return `${difference < 0 ? 'просрочено на ' : ''}${count} ${word}`;
}
const dates = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Moscow' });
const stamps = new Intl.DateTimeFormat('ru-RU', { dateStyle: 'long', timeStyle: 'short', timeZone: 'Europe/Moscow' });
const received = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Moscow' });
function stamp(label: string, value: string | null): HTMLElement {
  const item = node('span', 'pm-task-date'); item.append(node('span', 'pm-task-label', label));
  if (value) { const time = node('time', '', dates.format(new Date(value))); time.dateTime = value; time.title = stamps.format(new Date(value)); item.append(time); }
  else item.append(node('span', '', '—'));
  return item;
}

/** Own-session guidance only: it says nothing about a task's existence or PM permissions. */
export function renderPmReauthenticationHint(): HTMLElement {
  const hint = node('aside', 'pm-task-reauth'); hint.setAttribute('aria-label', 'Карточки задач PM');
  hint.append(node('p', '', 'Чтобы видеть карточки задач, повторно войдите через Ostart.'));
  const login = node('a', 'pm-task-reauth-login', 'Войти через Ostart'); login.href = '/v1/auth/zoan/start';
  hint.append(login);
  const consent = node('p', 'pm-task-reauth-consent', 'Для карточек также нужна связь с аккаунтом PM. ');
  const link = node('a', '', 'Связать аккаунт PM'); link.href = 'https://pm.openstart.ru/account/chat-link';
  link.target = '_blank'; link.rel = 'noopener noreferrer'; consent.append(link); hint.append(consent);
  return hint;
}

/** Same neutral fallback for denied, malformed and unavailable responses; no ACL/binding inference. */
export function renderPmTaskUnavailable(id: string, onRetry: () => void): HTMLElement {
  const hint = node('aside', 'pm-task-unavailable'); hint.setAttribute('aria-label', 'Карточка задачи PM');
  hint.append(node('p', '', 'Карточку загрузить не удалось.'));
  const actions = node('div', 'pm-task-failure-actions');
  const retry = node('button', 'pm-task-retry', 'Повторить'); retry.type = 'button';
  retry.addEventListener('click', () => { retry.disabled = true; onRetry(); });
  const open = node('a', '', 'Открыть задачу'); open.href = `https://pm.openstart.ru/${id}`;
  open.target = '_blank'; open.rel = 'noopener noreferrer'; actions.append(retry, open); hint.append(actions);
  const consent = node('p', 'pm-task-reauth-consent', 'Если аккаунт PM ещё не связан: ');
  const link = node('a', '', 'Связать аккаунт PM'); link.href = 'https://pm.openstart.ru/account/chat-link';
  link.target = '_blank'; link.rel = 'noopener noreferrer'; consent.append(link); hint.append(consent);
  return hint;
}

function refreshButton(onRefresh: () => void, disabled = false): HTMLButtonElement {
  const button = node('button', 'pm-task-refresh', '↻'); button.type = 'button'; button.disabled = disabled;
  button.title = 'Обновить карточку'; button.setAttribute('aria-label', 'Обновить карточку');
  button.addEventListener('click', onRefresh); return button;
}
export function renderPmTaskLoading(): HTMLElement {
  const loading = node('div', 'pm-task-loading'); loading.setAttribute('role', 'status');
  loading.append(node('span', '', 'Загрузка карточки…'), refreshButton(() => undefined, true));
  return loading;
}

export function renderPmTaskCard(task: PmTaskPreview, receivedAt: number, onRefresh: () => void,
  state: { refreshing?: boolean; refreshFailed?: boolean } = {}): HTMLElement {
  const card = node('section', 'pm-task-card'); card.setAttribute('aria-label', `Задача ${task.id}`);
  const heading = node('div', 'pm-task-heading');
  heading.append(picture(task.project.name, task.project.iconUrl, 'pm-task-project-icon'));
  const text = node('div', 'pm-task-heading-text');
  text.append(node('span', 'pm-task-project', task.project.name));
  const title = node('a', 'pm-task-title', task.title); title.href = task.canonicalUrl;
  title.target = '_blank'; title.rel = 'noopener noreferrer'; title.title = task.title;
  text.append(title); heading.append(text); card.append(heading);
  const summary = node('div', 'pm-task-summary'), deadline = stamp('Срок', task.deadlineAt);
  const remaining = taskDeadline(task.deadlineAt, Date.now(), task.status?.code === 'complete' || task.status?.code === 'confirm_testing');
  if (remaining) deadline.append(node('span', 'pm-task-deadline', `(${remaining})`));
  const effort = node('span', 'pm-task-effort', taskEffort(task.effort));
  effort.title = taskEffortLabel(task.effort); effort.setAttribute('aria-label', effort.title);
  const assignee = node('span', 'pm-task-assignee');
  assignee.setAttribute('role', 'group'); assignee.setAttribute('aria-label', `Исполнитель: ${task.assignee?.name ?? 'нет данных'}`);
  if (task.assignee) { assignee.append(picture(task.assignee.name, task.assignee.avatarUrl, 'pm-task-avatar'), node('span', 'pm-task-assignee-name', task.assignee.name)); }
  else assignee.append(node('span', '', '—'));
  const freshness = node('span', 'pm-task-received');
  if (task.status) {
    const status = node('span', 'pm-task-status', task.status.labels.ru); status.dataset.status = task.status.code;
    status.title = task.status.labels.ru; status.setAttribute('aria-label', task.status.labels.ru); summary.append(status);
  }
  const time = node('time', '', received.format(receivedAt).replace(', ', ' '));
  time.dateTime = new Date(receivedAt).toISOString(); time.title = `${stamps.format(receivedAt)} (Москва)`;
  freshness.setAttribute('aria-label', `Снимок получен ${stamps.format(receivedAt)} (Москва)`);
  freshness.append(time); summary.append(deadline, effort, assignee, freshness, refreshButton(onRefresh, state.refreshing)); card.append(summary);
  if (state.refreshing || state.refreshFailed) {
    const notice = node('p', 'pm-task-refresh-notice', state.refreshing ? 'Обновление снимка…' : 'Обновить не удалось. Показан сохранённый снимок.');
    notice.setAttribute('role', 'status'); card.append(notice);
  }
  if (task.status?.code === 'info_required' && task.clarifications?.items.length) {
    const clarifications = node('div', 'pm-task-clarifications');
    clarifications.append(node('span', 'pm-task-label', 'Нужно уточнить'));
    const list = node('ul', '');
    for (const item of task.clarifications.items) {
      const row = node('li', ''), link = node('a', '', item.text);
      link.href = item.url; link.target = '_blank'; link.rel = 'noopener noreferrer'; link.title = item.text;
      row.append(link); list.append(row);
    }
    clarifications.append(list);
    if (task.clarifications.hasMore) {
      const more = node('a', 'pm-task-clarifications-more', 'Все уточнения в PM');
      more.href = task.canonicalUrl; more.target = '_blank'; more.rel = 'noopener noreferrer'; clarifications.append(more);
    }
    card.append(clarifications);
  }
  return card;
}
